import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  InfoCallout,
  Input,
  RadioCard,
  RadioCardGroup,
  Row,
  Stack,
  Textarea,
} from '@leanin/ui';
import { Sprout } from 'lucide-react';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { PlacePicker, type PlaceSelection } from '@/components/geo';
import { useCreateCircle } from '@/lib/mutations/groups';
import { useOnboardingProgress } from '@/components/onboarding/progress';
import { circleSaveErrorMessage } from '@/components/circles/circle-save-error';

// Circle Leader onboarding tour — step 2 of 3.
//
// Submitting this step creates a real Circle row server-side with the
// caller as the leader. A successful create persists the new circle id
// into sessionStorage under CIRCLE_ID_KEY; if the leader bounces back to
// this step later, Next short-circuits to navigate-only so we don't
// double-create. (`affiliated`/`company` are collected for parity with
// /circles/new but not yet persisted — schema work pending.)
const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: "Your Circle's page" },
  { label: "You're in" },
];

const CIRCLE_ID_KEY = 'onboarding-cl:created-circle-id';

type YesNo = 'yes' | 'no' | null;
type Format = 'virtual' | 'in_person' | 'hybrid';

function readCreatedCircleId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(CIRCLE_ID_KEY);
}

export const Route = createFileRoute('/onboarding/cl/create-circle')({
  component: CLOnboardingCreateCirclePage,
});

function CLOnboardingCreateCirclePage() {
  const navigate = useNavigate();
  const createCircle = useCreateCircle();
  // An invited circle leader already leads the Circle they were invited to
  // (redeemed pre-onboarding by the Clerk webhook). This step creates a NEW
  // Circle, so for them it would make a duplicate — skip straight to the
  // completion screen, which deep-links to their actual Circle. A self-select
  // leader hasn't created a Circle yet, so `leaderCircleId` is null and they
  // see the form as normal.
  const { data: onboardingProgress } = useOnboardingProgress();
  const alreadyLeadsCircle = onboardingProgress?.leaderCircleId != null;
  useEffect(() => {
    if (alreadyLeadsCircle) {
      navigate({ to: '/onboarding/cl/complete' });
    }
  }, [alreadyLeadsCircle, navigate]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [place, setPlace] = useState<PlaceSelection | null>(null);
  const [affiliated, setAffiliated] = useState<YesNo>(null);
  const [company, setCompany] = useState('');
  const [openToNew, setOpenToNew] = useState<YesNo>(null);
  const [format, setFormat] = useState<Format>('virtual');
  const [error, setError] = useState<string | null>(null);
  const [createdCircleId, setCreatedCircleId] = useState<string | null>(
    () => readCreatedCircleId(),
  );

  // Gate Next on the required fields. Format defaults to 'virtual' so it
  // never gates on its own. `company` is only required when affiliated=yes.
  // If a circle was already created in this session, ignore form state
  // and let the user advance — they came back to this step but the
  // commit already happened.
  const canProceed =
    createdCircleId !== null ||
    (name.trim().length > 0 &&
      affiliated !== null &&
      (affiliated === 'no' || company.trim().length > 0) &&
      openToNew !== null);

  const isPending = createCircle.isPending;

  const handleNext = () => {
    if (!canProceed || isPending) return;
    setError(null);
    if (createdCircleId !== null) {
      // Already created in this onboarding session — just advance.
      navigate({ to: '/onboarding/cl/complete' });
      return;
    }
    createCircle.mutate(
      {
        name: name.trim(),
        ...(description.trim().length > 0
          ? { description: description.trim() }
          : {}),
        place,
        topicIds: [],
        format,
        access: 'public',
        // "Open to new members? Yes" → request-to-join; "No" → invite-only.
        joinPolicy: openToNew === 'yes' ? 'request' : 'invite_only',
        // `canProceed` already pinned `affiliated` to a non-null answer
        // and a 'yes' answer to a non-empty company name.
        affiliatedWithCompany: affiliated === 'yes',
        affiliatedCompany: affiliated === 'yes' ? company.trim() : null,
      },
      {
        onSuccess: (detail) => {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(CIRCLE_ID_KEY, detail.id);
          }
          setCreatedCircleId(detail.id);
          navigate({ to: '/onboarding/cl/complete' });
        },
        onError: (err) => {
          setError(circleSaveErrorMessage(err, 'Failed to create your Circle.'));
        },
      },
    );
  };

  return (
    <FlowShell
      role="Circle Leader"
      stepIndex={1}
      steps={STEPS}
      minutes={3}
      title="Tell us about your Circle."
      primary={
        <Button
          variant="primary"
          icon={I.arrow}
          disabled={!canProceed}
          loading={isPending}
          onClick={handleNext}
        >
          Save and continue
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/cl/welcome' })}
        >
          Back
        </Button>
      }
    >
      <Stack gap="lg">
        <InfoCallout tone="lilac" pad="default">
          <Row gap="xl" align="center">
            <div
              aria-hidden
              style={{
                width: 56,
                height: 56,
                borderRadius: 'var(--radius-full)',
                background: 'rgba(255, 255, 255, 0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'var(--palette-navy)',
              }}
            >
              <Sprout size={26} strokeWidth={1.75} />
            </div>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Heading level={3}>Tell us a little about your Circle.</Heading>
              <Body size="sm" color="mute">
                You can update everything later, even your Circle name.
              </Body>
            </Stack>
          </Row>
        </InfoCallout>

        <Card pad="compact">
          <Stack gap="sm">
            <Eyebrow>Basic details</Eyebrow>
            <Field required>
              <Field.Label>Circle name</Field.Label>
              <Field.Control>
                <Input
                  placeholder="Give your Circle a name."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                />
              </Field.Control>
            </Field>
            <Field>
              <Field.Label>Description</Field.Label>
              <Field.Control>
                <Textarea
                  rows={2}
                  placeholder="What is the Circle about? Who is it for?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                />
              </Field.Control>
            </Field>
            <Field>
              <Field.Label>Location</Field.Label>
              <Field.Control>
                <PlacePicker
                  mode="city"
                  value={place}
                  onChange={setPlace}
                  placeholder="Where do you expect your Circle to meet?"
                />
              </Field.Control>
            </Field>
            <Field required>
              <Field.Label>Is your Circle affiliated with a company?</Field.Label>
              <Field.Description>
                Select this option if your Circle is part of a formal program
                rolled out by your organization or because your Circle is made
                up of people from the same organization.
              </Field.Description>
              <Field.Control>
                <RadioCardGroup
                  fold="never"
                  value={affiliated ?? ''}
                  onValueChange={(v) => setAffiliated(v as 'yes' | 'no')}
                  columns={2}
                  aria-label="Is your Circle affiliated with a company?"
                >
                  <RadioCard value="yes" title="Yes" />
                  <RadioCard value="no" title="No" />
                </RadioCardGroup>
              </Field.Control>
            </Field>
            {affiliated === 'yes' ? (
              <Field label="Which company?" required>
                <Input
                  placeholder="e.g. Acme Inc."
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </Field>
            ) : null}
            <Field required>
              <Field.Label>Is this Circle open to new members?</Field.Label>
              <Field.Control>
                <RadioCardGroup
                  fold="never"
                  value={openToNew ?? ''}
                  onValueChange={(v) => setOpenToNew(v as 'yes' | 'no')}
                  columns={2}
                  aria-label="Is this Circle open to new members?"
                >
                  <RadioCard value="yes" title="Yes" />
                  <RadioCard value="no" title="No" />
                </RadioCardGroup>
              </Field.Control>
            </Field>
            <Field label="How will your Circle meet?" required>
              <RadioCardGroup
                fold="never"
                value={format}
                onValueChange={(v) => setFormat(v as Format)}
                columns={3}
                aria-label="Meeting format"
              >
                <RadioCard value="virtual" icon={I.globe} title="Virtually" />
                <RadioCard value="in_person" icon={I.pin} title="In-person" />
                <RadioCard value="hybrid" icon={I.users} title="Hybrid" />
              </RadioCardGroup>
            </Field>
          </Stack>
        </Card>

        {error !== null ? (
          <Card pad="compact" tone="burgundy">
            <Body size="sm" color="primary">{error}</Body>
          </Card>
        ) : null}
      </Stack>
    </FlowShell>
  );
}
